import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DataService } from 'src/app/services/data/data.service';
import { LocationStoreService } from 'src/app/stores/location-store/location-store.service';
import { IMapTemplate } from '../../csv-mapper/csv-map.class';
import { Charts, IBasicOpt, IChartModule } from '../charts.class';

@Component({
  selector: 'app-percentage-bar-chart',
  templateUrl: './percentage-bar-chart.component.html',
  styleUrls: ['./percentage-bar-chart.component.sass']
})
export class PercentageBarChartComponent implements OnInit, OnDestroy, IChartModule {

  @ViewChild('width') widthEle: ElementRef
  currentLocation;

  @Input() set data(_data: any[]) {
    this._data = _data
    setTimeout(() => {
      this.initComp$.next()
    }, 100)
  }

  /** Mapping will need to match whatver data is required to generate the chart */
  @Input() set mapping(_mapping: IMapTemplate) {
    this._mapping = _mapping
    setTimeout(() => {
      this.initComp$.next()
    }, 100)
  }

  @Input() set noDataMsg(msg: string) {
    this.ui.noDataMsg = msg
  }

  /**
   * Output event emitted when chart is clicked
   * TODO: For Charts Module
   * Set the type that's standardized across all IChartModule
   */
  @Output() chartClicked = new EventEmitter()

  /** Required to be ready by Charts Module */
  static SCHEMA = {
    /** To be filled when integrating with Charts module. Rn ignore it first */
  }

  /** ID for uniqueness (placeholder) */
  id: string
  /** Observable to trigger changes when mapping and data changes */
  initComp$: Subject<boolean> = new Subject<boolean>()
  /** Unsubber */
  unsubber: Subject<boolean> = new Subject<boolean>()

  ui: {
    echart?: any
    noDataMsg: string
    noData?: boolean
  } = {
    noDataMsg: this._t.instant('No data is present'),
    noData: true
  }

  _mapping: IMapTemplate
  _data: any[]


  SCHEMA = {} as IMapTemplate

  constructor(
    private _t : TranslateService,
    private dataService: DataService,
    private locationStore: LocationStoreService
  ) {}

  ngOnInit(): void {
    this.initComp$
      .pipe(takeUntil(this.unsubber.asObservable()))
      .subscribe((res) => {
        this.locationStore.currLocation$
          .pipe(takeUntil(this.unsubber.asObservable()))
          .subscribe(currLoc => {
            if (currLoc.id) this.generateChart(currLoc) 
          })
      })
  }

  ngOnDestroy(): void {
    this.unsubber.next()
    this.unsubber.unsubscribe()
  }

  generateChart(currLoc?) {
    const mapping = this._mapping
    if (currLoc?.generic_type_template?.annotation_set) {
      this._data = currLoc.generic_type_template.annotation_set.map(i => {
        return this._data?.find(s => s?.id === i)
      })
    }
    const data: ITempCompData[] = this._data

    if (!mapping || !data) return

    // if (!this.widthEle || !this.widthEle.nativeElement) debugger
    const containerWidth = this.widthEle?.nativeElement?.offsetWidth

    /** Skips mapping processing */
    const totalCount = data.reduce((total, ele) => {
      if (ele) total += ele.count
      return total
    }, 0)

    /** Default object. Should remain constant */
    const defObj: IBasicOpt = {
      visual: {
        xaxisname: '',
        yaxisname: '',
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'none'
          },
          formatter: (params) => {
            // return `${Math.round(params[0].value)}%`
            return `${params[0].value.toFixed(2)}%`
          }
        },
        xAxis: {
          show: false,
          axisLabel: {
            interval: 0
          },
          max: 100
        },
        yAxis: {
          /** 
           * This forces the axis to show all its values
           * TODO: Changes depending on the requirement
           * Found in: https://stackoverflow.com/a/44295925
           */
          axisLabel: {
            interval: 0
          },
          show: false
        },
        isHorizontal: false,
        boundaryGap: true,
        categoryData: [],
        grid: {
          top: '5%',
          left: '5%',
          bottom: '5%',
          width: '90%'
        },
        borderRadius: 5
      },
      series: [
        {
          name: 'Percentage',
          type: 'bar',
          color: '#1B9BBB',
          data: [],
          stack: 'percent',
          showBackground: true,
          backgroundStyle: {
            color: '#6E6E6E',
            barBorderRadius: 5
          },
          label: {
            show: true,
            color: 'white',
            position: [(0.85 * containerWidth), '30%'],
            // position: [100, '30%'],
            formatter: '{b}',
            align: 'right'
          }
        }
        // {
        //   name: 'Remainder',
        //   type: 'bar',
        //   data: [],
        //   color: '#6E6E6E',
        //   label: {
        //     show: true,
        //     formatter: '{b}',
        //     position: 'insideRight'
        //   },
        //   stack: 'percent'
        // }
      ]
    }
    const workObj = { ...defObj }
    const visual = workObj.visual
    const series = workObj.series

    visual.isHorizontal = true

    data.map(ele => {
      visual.categoryData.push(ele.name)
      const percent = (ele.count / totalCount * 100)
      const val1 = percent, val2 = (100 - percent)

      if (this.ui.noData) this.ui.noData = !val1
      const defObj = {
        value: 0,
        id: ele.id,
        name: this._t.instant(ele.name)
      }
      series[0].data.push({ ...defObj, ...{ value: val1 } })
      // series[1].data.push({ ...defObj, ...{ value: val2 } })
    })

    this.ui.echart = Charts.genLineBarOpt(workObj)
  }

  /** Emit out event when chart is clicked */
  onChartClicked(evt) {
    /**
     * TODO:
     * Emit the event together when refactoring for Charts Module
     */
    this.chartClicked.emit(evt)
  }
}

/** 
 * Interface for this specific component 
 * To be replaced with mapping
 */
interface ITempCompData {
  id: string
  name: string
  count: number
}
